// ══════════════════════════════════════════════════════════════════════════════════════════════
//  B-2254 · LA CUENTA DEL PELLIZCO: AMPLIAR UN DOCUMENTO CON DOS DEDOS                        (27-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «Ver problema con la apertura de documentos en iPhone: cuando se abre un PDF o documento del estilo, no se
//  puede pellizcar la pantalla para aumentar o disminuir».
//
//  POR QUÉ NO SE PODÍA. Las dos apps del iPhone (la del equipo y la del paciente) son Capacitor, y Capacitor apaga el
//  pellizco de su vista web mientras no se le diga `zoomEnabled` (node_modules/@capacitor/ios, WebViewDelegationHandler:
//  `scrollViewWillBeginZooming` desactiva el reconocedor del pellizco). Encima, B-838 (utils/zoomIos.js) pone
//  `maximum-scale=1.0` en iOS para que la app no se acerque sola al tocar un campo, y la vista web de una app SÍ lo
//  respeta (Safari no: allí el pellizco de accesibilidad pasa por encima). Y dentro de la app todo documento se abre en
//  el visor propio (components/VisorDocumento, components/VisorImagen), que no traía zoom: el PDF pintado con pdf.js
//  y la foto se quedaban a tamaño de pantalla, sin forma de acercarlos.
//
//  EL ARREGLO NO TOCA NINGUNO DE LOS DOS CANDADOS. Abrir el zoom nativo de la app pediría publicar otra versión en la App
//  Store, y volvería a dejar la pantalla acercada y cortada al escribir (B-838). Lo que se hace es que CADA VISOR LLEVE
//  SU PROPIO ZOOM (utils/zonaAmpliable.jsx): dos dedos acercan y alejan hacia donde están, un dedo se mueve por lo
//  ampliado con el desplazamiento de siempre, el doble toque alterna entre ajustado y ampliado, y en el ordenador la
//  rueda con Ctrl (o el pellizco del trackpad) y los botones − / + hacen lo mismo. Funciona igual en la app ya
//  instalada (sin publicar nada), en Safari y en Android.
//
//  Aquí solo está la cuenta, sin React ni pantalla, para poder ejecutarla tal cual en la guardia
//  (backend/routes/zoom-documentos-iphone.test.js).
// ══════════════════════════════════════════════════════════════════════════════════════════════

export const ZOOM_MIN = 1
export const ZOOM_MAX = 5
// El doble toque lleva aquí desde «ajustado a la pantalla»; desde cualquier otro zoom, vuelve a ajustado.
export const ZOOM_DOBLE_TOQUE = 2.5
// Lo que acerca o aleja cada pulsación de los botones − / +.
export const PASO_BOTON = 1.5
// Dos toques cuentan como doble toque si llegan así de seguidos y así de cerca.
export const DOBLE_TOQUE_MS = 320
export const DOBLE_TOQUE_PX = 30
// Un dedo que se mueve más que esto ya no está tocando: está desplazando.
export const TOQUE_QUIETO_PX = 10

// El zoom dentro de sus límites. Lo que no es un número vuelve a «ajustado».
export function limitarZoom(z, max = ZOOM_MAX) {
  const n = Number(z)
  const tope = Math.max(ZOOM_MIN, Number(max) || ZOOM_MAX)
  if (!Number.isFinite(n)) return ZOOM_MIN
  return Math.min(Math.max(n, ZOOM_MIN), tope)
}

// La distancia entre dos dedos y el punto que queda entre los dos (en coordenadas de pantalla).
export const distancia = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
export const centro = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 })

// El punto del documento (medido a zoom 1) que está debajo del punto de pantalla `p`.
export function puntoDelContenido(rectContenido, p, zoom) {
  const z = zoom > 0 ? zoom : 1
  return { x: (p.x - rectContenido.left) / z, y: (p.y - rectContenido.top) / z }
}

// Cuánto hay que desplazar para que ese punto del documento vuelva a quedar debajo de `p` con el zoom nuevo. Es lo que
// hace que el documento se acerque HACIA LOS DEDOS y no hacia la esquina de arriba.
export function correccion(rectContenidoNuevo, punto, zoom, p) {
  return { dx: rectContenidoNuevo.left + punto.x * zoom - p.x, dy: rectContenidoNuevo.top + punto.y * zoom - p.y }
}

// Empieza un pellizco: guarda la distancia entre los dedos, el zoom de partida y el punto del documento entre los dos.
export function iniciarPellizco(toques, zoom, rectContenido) {
  const [a, b] = toques
  return { d0: Math.max(1, distancia(a, b)), z0: limitarZoom(zoom, Infinity), punto: puntoDelContenido(rectContenido, centro(a, b), zoom) }
}

// Los dedos se han movido: el zoom crece en la misma proporción que la distancia entre ellos.
export function moverPellizco(estado, toques, max = ZOOM_MAX) {
  const [a, b] = toques
  return { zoom: limitarZoom(estado.z0 * distancia(a, b) / estado.d0, max), centro: centro(a, b) }
}

// La rueda con Ctrl (y el pellizco del trackpad, que el navegador manda así): suave, y la misma con ratón o trackpad.
export function zoomDeRueda(zoom, deltaY, max = ZOOM_MAX) {
  const d = Math.max(-100, Math.min(100, Number(deltaY) || 0))
  return limitarZoom(zoom * Math.exp(-d * 0.01), max)
}

// El doble toque: de ajustado, a ampliado; de cualquier otro zoom, a ajustado.
export const zoomDeDobleToque = (zoom, max = ZOOM_MAX) => (zoom > ZOOM_MIN + 0.05 ? ZOOM_MIN : limitarZoom(ZOOM_DOBLE_TOQUE, max))

// ¿Este toque y el anterior son un doble toque?
export function esDobleToque(anterior, ahora) {
  return !!(anterior && ahora && ahora.t - anterior.t >= 0 && ahora.t - anterior.t <= DOBLE_TOQUE_MS
    && Math.hypot(ahora.x - anterior.x, ahora.y - anterior.y) <= DOBLE_TOQUE_PX)
}

// El ancho del documento a zoom 1 («ajustado»): todo el ancho de la zona (menos su margen), sin pasar de `anchoMax`
// (un PDF en una pantalla grande no se estira a 2.000 px) y, si se sabe su proporción (alto / ancho, una foto), sin
// pasar tampoco del alto: la foto cabe entera.
export function anchoBase({ ancho, alto, proporcion, anchoMax, margen = 0 }) {
  let base = Math.max(1, (Number(ancho) || 0) - 2 * margen)
  if (proporcion > 0 && alto > 0) base = Math.min(base, Math.max(1, (alto - 2 * margen) / proporcion))
  if (anchoMax > 0) base = Math.min(base, anchoMax)
  return Math.max(1, Math.floor(base))
}

// AL AMPLIAR UN PDF SE VUELVE A PINTAR LA PÁGINA CON MÁS PÍXELES, o la letra pequeña sale borrosa justo cuando se
// acerca para leerla. Con un tope: un lienzo de iOS no pasa de unos 16 millones de píxeles, y un teléfono no tiene
// memoria para cuarenta páginas así. Se pinta a lo que pide el zoom, sin pasar de PIXELES_MAX_PAGINA ni de ANCHO_MAX_LIENZO.
export const PIXELES_MAX_PAGINA = 8000000
export const ANCHO_MAX_LIENZO = 4096
export function anchoDePintado({ anchoCss, zoom = 1, dpr = 1, proporcion = 0 }) {
  const deseado = Math.max(1, Number(anchoCss) || 1) * Math.max(1, Number(zoom) || 1) * Math.max(1, Number(dpr) || 1)
  const porArea = proporcion > 0 ? Math.sqrt(PIXELES_MAX_PAGINA / proporcion) : deseado
  return Math.max(1, Math.floor(Math.min(deseado, porArea, ANCHO_MAX_LIENZO)))
}
